import React, { useState, useEffect, useCallback } from 'react';
import { fetchTradeHistory, fetchAccounts } from '../api';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function getCalendarWeeks(year, month) {
  // month is 1-based
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const weeks = [];
  let week = new Array(7).fill(null);
  for (let d = 1; d <= last.getDate(); d++) {
    const dt = new Date(year, month - 1, d);
    const dow = (dt.getDay() + 6) % 7; // Mon=0 .. Sun=6
    week[dow] = d;
    if (dow === 6 || d === last.getDate()) {
      weeks.push(week);
      week = new Array(7).fill(null);
    }
  }
  return weeks;
}

function formatPnl(v) {
  if (v == null || v === 0) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}`;
}

export default function TradeHistory() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [monthData, setMonthData] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dayLoading, setDayLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filterAccount, setFilterAccount] = useState(0); // 0 = All

  // Load broker accounts
  useEffect(() => {
    fetchAccounts().then(r => {
      if (r.data?.accounts) setAccounts(r.data.accounts);
    }).catch(() => {});
  }, []);

  // Load month data
  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month: `${year}-${String(month).padStart(2,'0')}` };
      if (filterAccount) params.account_id = filterAccount;
      const r = await fetchTradeHistory(params);
      if (r.data?.status === 'ok') {
        setMonthData(r.data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [year, month, filterAccount]);

  useEffect(() => {
    loadMonth();
    setDayData(null);
    setSelectedDate(null);
  }, [loadMonth]);

  // Load day detail
  const loadDay = async (dateStr) => {
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      setDayData(null);
      return;
    }
    setSelectedDate(dateStr);
    setDayLoading(true);
    try {
      const params = { date: dateStr };
      if (filterAccount) params.account_id = filterAccount;
      const r = await fetchTradeHistory(params);
      if (r.data?.status === 'ok') setDayData(r.data);
    } catch { /* ignore */ }
    setDayLoading(false);
  };

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  // Build day→pnl map
  const dayPnlMap = {};
  if (monthData?.days) {
    for (const d of monthData.days) {
      dayPnlMap[d.date] = d;
    }
  }

  const weeks = getCalendarWeeks(year, month);
  const monthStr = `${year}-${String(month).padStart(2,'0')}`;

  return (
    <div className="th-page">
      <h2 className="th-title">Trade History</h2>

      {/* Filters row */}
      <div className="th-controls">
        <div className="th-month-nav">
          <button className="th-nav-btn" onClick={prevMonth}>◀</button>
          <span className="th-month-label">{MONTH_NAMES[month - 1]} {year}</span>
          <button className="th-nav-btn" onClick={nextMonth}>▶</button>
        </div>
        <div className="th-filters">
          <label className="th-filter-label">Account</label>
          <select value={filterAccount} onChange={e => setFilterAccount(Number(e.target.value))}>
            <option value={0}>All Accounts</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name} {a.paper_trading ? '(Paper)' : '(Live)'}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Month summary cards */}
      {monthData && (
        <div className="th-summary">
          <div className={`th-stat-card ${(monthData.month_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
            <div className="th-stat-label">Month P&L</div>
            <div className="th-stat-value">{formatPnl(monthData.month_pnl)}</div>
          </div>
          <div className="th-stat-card">
            <div className="th-stat-label">Trading Days</div>
            <div className="th-stat-value">{monthData.trading_days || 0}</div>
          </div>
          <div className="th-stat-card positive">
            <div className="th-stat-label">Winning Days</div>
            <div className="th-stat-value">{monthData.winning_days || 0}</div>
          </div>
          <div className="th-stat-card negative">
            <div className="th-stat-label">Losing Days</div>
            <div className="th-stat-value">{monthData.losing_days || 0}</div>
          </div>
          <div className="th-stat-card">
            <div className="th-stat-label">Total Trades</div>
            <div className="th-stat-value">{monthData.month_trades || 0}</div>
          </div>
          <div className="th-stat-card">
            <div className="th-stat-label">Win Rate</div>
            <div className="th-stat-value">
              {monthData.trading_days ? ((monthData.winning_days / monthData.trading_days) * 100).toFixed(0) : 0}%
            </div>
          </div>
        </div>
      )}

      {/* Per-account month summary */}
      {monthData?.accounts && monthData.accounts.length > 0 && (
        <div className="th-account-summary">
          <h4 className="th-section-title">Per-Account Summary</h4>
          <div className="th-account-cards">
            {monthData.accounts.map((a, i) => (
              <div key={i} className={`th-account-card ${a.pnl >= 0 ? 'positive' : 'negative'}`}>
                <div className="th-account-name">{a.account_name}</div>
                <div className="th-account-pnl">{formatPnl(a.pnl)}</div>
                <div className="th-account-days">{a.trading_days} trading day{a.trading_days !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar heatmap */}
      <div className="th-calendar">
        {loading ? (
          <div className="th-loading">Loading...</div>
        ) : (
          <table className="th-cal-table">
            <thead>
              <tr>
                {DAY_LABELS.map(d => <th key={d}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map((day, di) => {
                    if (day === null) return <td key={di} className="th-cal-empty" />;
                    const dateStr = `${monthStr}-${String(day).padStart(2,'0')}`;
                    const info = dayPnlMap[dateStr];
                    const isSelected = selectedDate === dateStr;
                    const pnl = info?.pnl || 0;
                    let cls = 'th-cal-cell';
                    if (info) cls += pnl >= 0 ? ' th-cal-win' : ' th-cal-loss';
                    else cls += ' th-cal-no-trade';
                    if (isSelected) cls += ' th-cal-selected';
                    return (
                      <td key={di} className={cls} onClick={() => info && loadDay(dateStr)}>
                        <div className="th-cal-day">{day}</div>
                        {info ? (
                          <>
                            <div className={`th-cal-pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
                              {formatPnl(pnl)}
                            </div>
                            <div className="th-cal-trades">{info.trades} trade{info.trades !== 1 ? 's' : ''}</div>
                            {info.accounts && info.accounts.length > 1 && (
                              <div className="th-cal-accounts">{info.accounts.length} accts</div>
                            )}
                          </>
                        ) : (
                          <div className="th-cal-no-data">—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Day drill-down */}
      {selectedDate && (
        <div className="th-day-detail">
          <h3 className="th-day-title">
            {new Date(selectedDate + 'T00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
            {dayData && (
              <span className={`th-day-pnl ${(dayData.day_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatPnl(dayData.day_pnl)} pts
              </span>
            )}
          </h3>

          {/* Day per-account breakdown */}
          {dayData?.accounts && dayData.accounts.length > 0 && (
            <div className="th-day-accounts">
              {dayData.accounts.map((a, i) => (
                <span key={i} className={`th-day-acct-tag ${a.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {a.account_name}: {formatPnl(a.pnl)} ({a.trades} trade{a.trades !== 1 ? 's' : ''})
                </span>
              ))}
            </div>
          )}

          {dayLoading ? (
            <div className="th-loading">Loading trades...</div>
          ) : dayData?.strategies?.length > 0 ? (
            dayData.strategies.map((strat, si) => (
              <div key={si} className="th-strat-block">
                <div className="th-strat-header">
                  <span className="th-strat-name">{strat.display_name}</span>
                  {strat.account_name && (
                    <span className="th-strat-account">{strat.account_name}</span>
                  )}
                  <span className={`th-strat-pnl ${strat.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnl(strat.total_pnl)} pts
                  </span>
                  {strat.paper_trade && <span className="th-paper-tag">Paper</span>}
                </div>
                <div className="th-table-wrapper">
                  <table className="th-trade-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Strike</th>
                        <th>Entry</th>
                        <th>Exit</th>
                        <th>CE Entry</th>
                        <th>PE Entry</th>
                        <th>CE Exit</th>
                        <th>PE Exit</th>
                        <th>CE P&L</th>
                        <th>PE P&L</th>
                        <th>Total</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {strat.trades.map((t, ti) => (
                        <tr key={ti}>
                          <td>{t.trade_no}</td>
                          <td className="mono">{t.strike}</td>
                          <td className="mono">{t.entry_time}</td>
                          <td className="mono">{t.exit_time}</td>
                          <td className="mono">{t.ce_entry ? t.ce_entry.toFixed(2) : '—'}</td>
                          <td className="mono">{t.pe_entry ? t.pe_entry.toFixed(2) : '—'}</td>
                          <td className="mono">{t.ce_exit ? t.ce_exit.toFixed(2) : '—'}</td>
                          <td className="mono">{t.pe_exit ? t.pe_exit.toFixed(2) : '—'}</td>
                          <td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{formatPnl(t.ce_pnl)}</td>
                          <td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{formatPnl(t.pe_pnl)}</td>
                          <td className={`th-total ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                            {formatPnl(t.total_pnl)}
                          </td>
                          <td className="th-reason">{t.exit_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          ) : (
            <div className="th-no-trades">No trades found for this day.</div>
          )}
        </div>
      )}
    </div>
  );
}
